import Logger from '../lib/logger';
import event from '../lib/mixins/event';
import type { EventEmitter } from '../lib/mixins/event';

const SEEN_COOKIE = 'b2b_welcome_seen';

// Long enough that the dialog reads as "shown once on sign-in" across a working
// session, short enough that a buyer returning next week is welcomed again.
const SEEN_MAX_AGE_SECONDS = 60 * 60 * 12;

/** Object.assign below grafts the event mixin onto the prototype at runtime. */
interface B2bWelcomeDialog extends EventEmitter {}

class B2bWelcomeDialog {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	NAME: string;
	dom: HTMLDialogElement;
	closeButton: HTMLElement | null;
	closeHandler?: () => void;
	backdropHandler?: (e: MouseEvent) => void;

	/**
	 * Class constructor
	 */
	constructor(dom: HTMLDialogElement) {

		this.NAME = 'B2bWelcomeDialog';

		this.dom = dom;
		this.closeButton = this.dom.querySelector<HTMLElement>('[data-b2b-welcome-close]');
	}

	/**
	 * init - Show the dialog on the buyer's first wholesale page view
	 */
	init() {
		Logger.info(`${this.NAME} > init`);

		this.closeHandler = () => this.dom.close();
		if (this.closeButton) this.closeButton.addEventListener('click', this.closeHandler);

		// <dialog> puts its ::backdrop inside the dialog's own hit area, so a click
		// that lands on the element itself rather than on the inner panel is a
		// click on the backdrop.
		this.backdropHandler = (e) => {
			if (e.target === this.dom) this.dom.close();
		};
		this.dom.addEventListener('click', this.backdropHandler);

		if (this.hasBeenSeen()) return;

		this.markSeen();
		this.dom.showModal();
	}

	/**
	 * hasBeenSeen - Whether this browser has already been welcomed
	 */
	hasBeenSeen() {
		return document.cookie.split('; ').some(entry => entry.startsWith(`${SEEN_COOKIE}=`));
	}

	/**
	 * markSeen - Record the welcome so the next page view stays quiet
	 *
	 * Written before the dialog opens rather than on close: a buyer who navigates
	 * away without closing has still seen it, and reopening it on every page for
	 * them would be worse than never showing it at all.
	 */
	markSeen() {
		const secure = window.location.protocol === 'https:' ? '; Secure' : '';
		document.cookie = `${SEEN_COOKIE}=1; Max-Age=${SEEN_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
	}

	/**
	 * destroy - Clean up event listeners
	 */
	destroy() {
		Logger.info(`${this.NAME} > destroy`);

		if (this.closeButton && this.closeHandler) {
			this.closeButton.removeEventListener('click', this.closeHandler);
		}

		if (this.backdropHandler) {
			this.dom.removeEventListener('click', this.backdropHandler);
		}
	}

}

Object.assign(B2bWelcomeDialog.prototype, event);

export default B2bWelcomeDialog;
